'use client';

export type EmailEventType =
  | 'enrollment_confirmation' |'interview_scheduled' |'training_completed' |'opportunity_assigned';

export interface SimulatedEmail {
  id: string;
  type: EmailEventType;
  to: string;
  subject: string;
  preview: string;
  body: string;
  sentAt: string;
  status: 'delivered' | 'pending' | 'failed';
}

const EMAIL_STORAGE_KEY = 'fca_email_log';

function generateEmailId(): string {
  return `email_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function saveEmail(email: SimulatedEmail): void {
  if (typeof window === 'undefined') return;
  try {
    const existing: SimulatedEmail[] = JSON.parse(localStorage.getItem(EMAIL_STORAGE_KEY) || '[]');
    existing.unshift(email);
    localStorage.setItem(EMAIL_STORAGE_KEY, JSON.stringify(existing.slice(0, 100)));
  } catch {}
}

export function getEmailLog(): SimulatedEmail[] {
  if (typeof window === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem(EMAIL_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

export function clearEmailLog(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(EMAIL_STORAGE_KEY);
}

// --- Email senders ---

export function sendEnrollmentConfirmation(params: {
  to: string;
  name: string;
  transactionId: string;
  amount: number;
  cardBrand?: string;
  cardLast4?: string;
}): SimulatedEmail {
  const email: SimulatedEmail = {
    id: generateEmailId(),
    type: 'enrollment_confirmation',
    to: params.to,
    subject: '🎉 Welcome to Career Hub — Enrollment Confirmed',
    preview: `Hi ${params.name}, your Career Hub enrollment is confirmed. Transaction ID: ${params.transactionId}`,
    body: `Hi ${params.name},

Your Career Hub enrollment is confirmed! You now have full access to your AI interview, training modules, and opportunity matching.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PAYMENT RECEIPT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Amount Charged:   $${params.amount.toFixed(2)}
Transaction ID:   ${params.transactionId}
${params.cardBrand && params.cardLast4 ? `Card:             ${params.cardBrand} ····${params.cardLast4}` : ''}
Date:             ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

NEXT STEPS:
1. Complete your AI Interview (10–15 min)
2. Work through your training modules
3. Get matched with remote sales opportunities

Questions? Reply to this email anytime.

— The Free Crypto Academy Team`,
    sentAt: new Date().toISOString(),
    status: 'delivered',
  };
  saveEmail(email);
  return email;
}

export function sendInterviewScheduled(params: {
  to: string;
  name: string;
  scheduledDate?: string;
}): SimulatedEmail {
  const date = params.scheduledDate || new Date(Date.now() + 86400000 * 2).toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
  const email: SimulatedEmail = {
    id: generateEmailId(),
    type: 'interview_scheduled',
    to: params.to,
    subject: '📅 Your AI Interview Has Been Scheduled',
    preview: `Hi ${params.name}, your AI interview is scheduled for ${date}. Here's what to expect.`,
    body: `Hi ${params.name},

Your AI Interview has been scheduled! This is a guided 10–15 minute conversation that builds your candidate profile and helps match you with the right remote sales opportunities.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
INTERVIEW DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Date:     ${date}
Format:   AI-guided voice/text interview
Duration: ~10–15 minutes
Location: Your Member Dashboard → AI Interview tab
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

WHAT TO EXPECT:
• Questions about your sales experience and goals
• Availability and equipment check
• Industry preferences
• Communication style assessment

TIPS FOR SUCCESS:
✓ Find a quiet space with minimal background noise
✓ Have your headset or earbuds ready
✓ Be honest — there are no wrong answers
✓ Speak naturally, as if talking to a real recruiter

Your responses will be used to generate your candidate profile and readiness score.

— The Free Crypto Academy Team`,
    sentAt: new Date().toISOString(),
    status: 'delivered',
  };
  saveEmail(email);
  return email;
}

export function sendTrainingCompleted(params: {
  to: string;
  name: string;
  courseName: string;
  completionDate?: string;
  certificateId?: string;
}): SimulatedEmail {
  const date = params.completionDate || new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
  const certId = params.certificateId || `CERT-${Date.now().toString(36).toUpperCase()}`;
  const email: SimulatedEmail = {
    id: generateEmailId(),
    type: 'training_completed',
    to: params.to,
    subject: `✅ Training Complete — ${params.courseName}`,
    preview: `Congratulations ${params.name}! You've completed ${params.courseName}. Your certificate is ready.`,
    body: `Congratulations, ${params.name}!

You've successfully completed "${params.courseName}". This is a major milestone in your journey to becoming opportunity-eligible.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
COMPLETION CERTIFICATE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Course:           ${params.courseName}
Completed:        ${date}
Certificate ID:   ${certId}
Issued by:        Free Crypto Academy
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

WHAT'S NEXT:
${params.courseName.includes('Remote Sales') || params.courseName.includes('Sales')
  ? '→ You are now eligible for opportunity matching!\n→ Check your Opportunities tab for available remote sales roles.' :'→ Continue to your next training module\n→ Complete all modules to unlock opportunity matching'}

You can download your certificate from the Certifications tab in your Member Dashboard.

Keep up the great work!

— The Free Crypto Academy Team`,
    sentAt: new Date().toISOString(),
    status: 'delivered',
  };
  saveEmail(email);
  return email;
}

export function sendOpportunityAssigned(params: {
  to: string;
  name: string;
  opportunityName: string;
  assignedBy?: string;
}): SimulatedEmail {
  const email: SimulatedEmail = {
    id: generateEmailId(),
    type: 'opportunity_assigned',
    to: params.to,
    subject: `🚀 New Opportunity Assigned — ${params.opportunityName}`,
    preview: `Great news, ${params.name}! You've been matched with ${params.opportunityName}. Review the details and confirm your interest.`,
    body: `Great news, ${params.name}!

Based on your AI interview results and training completion, you've been matched with a new remote sales opportunity.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
OPPORTUNITY DETAILS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Opportunity:      ${params.opportunityName}
Assigned by:      ${params.assignedBy || 'Free Crypto Academy Team'}
Date:             ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
Status:           Pending Your Confirmation
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

NEXT STEPS:
1. Log in to your Member Dashboard
2. Navigate to the Opportunities tab
3. Review the full opportunity details
4. Confirm your interest within 48 hours

This opportunity was selected based on your candidate profile, readiness score, and availability. Confirming your interest does not guarantee placement — a final review by the hiring team is required.

Act fast — opportunities are filled on a first-come, first-served basis.

— The Free Crypto Academy Team`,
    sentAt: new Date().toISOString(),
    status: 'delivered',
  };
  saveEmail(email);
  return email;
}
